import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Send, 
  CheckCircle, 
  Loader2, 
  Building2, 
  Truck, 
  Landmark, 
  Briefcase,
  ArrowLeft,
  Brain,
  CreditCard,
  Shield,
  Camera,
  FileCheck,
  Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const companyTypes = [
  { id: 'healthcare_provider', label: 'Healthcare Provider', icon: Building2, desc: 'Hospitals, clinics, dialysis centers' },
  { id: 'transportation_company', label: 'Transportation Company', icon: Truck, desc: 'NEMT providers, fleet operators' },
  { id: 'government', label: 'Government Agency', icon: Landmark, desc: 'HHS, Medicaid, municipal transit' },
  { id: 'broker', label: 'Broker', icon: Briefcase, desc: 'Transportation brokers, MCOs' },
];

const serviceOptions = [
  { id: 'dispatch', label: 'AI Dispatch (OVA)', icon: Brain },
  { id: 'payments', label: 'Medex Pay', icon: CreditCard },
  { id: 'insurance', label: 'Group Insurance', icon: Shield },
  { id: 'cameras', label: 'NEMT Vision Cameras', icon: Camera },
  { id: 'credentialing', label: 'Credentialing System', icon: FileCheck },
];

const fleetSizeOptions = [
  'No fleet (Healthcare Provider)',
  '1-10 vehicles',
  '11-50 vehicles',
  '51-100 vehicles',
  '100+ vehicles',
];

const tripVolumeOptions = [
  'Less than 100/month',
  '100-500/month',
  '500-2,000/month',
  '2,000-10,000/month',
  '10,000+/month',
];

const howHeardOptions = [
  'Web Search',
  'Industry Conference',
  'Referral',
  'Social Media',
  'News/Press',
  'Other',
];

export const PartnerApplicationForm = ({ onBack }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: '',
    email: '',
    phone: '',
    company_name: '',
    company_type: '',
    fleet_size: '',
    service_area: '',
    current_trip_volume: '',
    website: '',
    services_interested: [],
    message: '',
    how_heard: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services_interested: prev.services_interested.includes(serviceId)
        ? prev.services_interested.filter(s => s !== serviceId)
        : [...prev.services_interested, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_name || !formData.email || !formData.company_name || !formData.company_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/partners/apply`, formData);
      setApplicationId(response.data.id);
      setShowVerification(true);
      toast.success('Please check your email for verification code');
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setIsVerifying(true);

    try {
      await axios.post(`${API}/partners/verify`, {
        email: formData.email,
        code: verificationCode,
        application_id: applicationId
      });
      setIsVerified(true);
      toast.success('Email verified successfully!');
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await axios.post(`${API}/partners/resend-code?email=${formData.email}&application_id=${applicationId}`);
      toast.success('New verification code sent');
    } catch (error) {
      toast.error('Failed to resend code');
    }
  };

  // Email Verification Screen
  if (showVerification && !isVerified) {
    return (
      <div className="max-w-md mx-auto" data-testid="verification-screen">
        <div className="text-center mb-8">
          <button
            onClick={() => setShowVerification(false)}
            className="inline-flex items-center text-slate-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Form
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-neon-cyan/20 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-neon-cyan" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-white mb-3">
            Verify Your Email
          </h3>
          <p className="text-slate-400 mb-6">
            We sent a 6-digit code to <span className="text-white">{formData.email}</span>
          </p>
          
          <div className="mb-6">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full input-dark text-center text-3xl font-mono tracking-[0.5em] py-4"
              maxLength={6}
              data-testid="verification-code-input"
            />
          </div>

          <button
            onClick={handleVerify}
            disabled={isVerifying || verificationCode.length !== 6}
            className="w-full btn-primary mb-4 disabled:opacity-50"
            data-testid="verify-button"
          >
            {isVerifying ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Verifying...
              </span>
            ) : (
              'Verify Email'
            )}
          </button>

          <button
            onClick={handleResendCode}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Didn't receive the code? Resend
          </button>
        </motion.div>
      </div>
    );
  }

  // Success Screen (after verification)
  if (isVerified) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-8 text-center max-w-2xl mx-auto"
        data-testid="partner-success"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-10 h-10 text-neon-green" />
        </motion.div>
        <h3 className="text-3xl font-heading font-bold text-white mb-4">
          Application Verified!
        </h3>
        <p className="text-slate-400 mb-6 text-lg">
          Thank you for verifying your email. Our team will review your application and contact you within 24-48 hours.
        </p>
        <div className="glass p-4 rounded-lg mb-6 text-left">
          <h4 className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-2">What's Next?</h4>
          <ol className="text-sm text-slate-300 space-y-2">
            <li>1. Our partnership team reviews your application</li>
            <li>2. We'll schedule an introductory call</li>
            <li>3. Begin the credentialing process</li>
            <li>4. Onboard to the MedexOVA platform</li>
          </ol>
        </div>
        <button
          onClick={onBack}
          className="btn-secondary"
        >
          Return to Home
        </button>
      </motion.div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="partner-application-form">
      {/* Header */}
      <div className="text-center mb-8">
        <button
          onClick={onBack}
          className="inline-flex items-center text-slate-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
          Partner Application
        </h2>
        <p className="text-slate-400 max-w-xl mx-auto">
          Join the MedexOVA network. Complete this form and our team will reach out to discuss how we can work together.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= s ? 'bg-neon-cyan text-black' : 'bg-slate-800 text-slate-500'
              }`}
            >
              {s}
            </div>
            {s < 3 && (
              <div className={`w-16 h-1 mx-2 rounded ${step > s ? 'bg-neon-cyan' : 'bg-slate-800'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="glass-card p-6 lg:p-8">
        {/* Step 1: Company Type */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3 className="text-xl font-heading font-semibold text-white mb-6">
              What type of organization are you?
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {companyTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, company_type: type.id }))}
                  className={`p-6 rounded-xl border text-left transition-all ${
                    formData.company_type === type.id
                      ? 'bg-neon-cyan/10 border-neon-cyan/50'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <type.icon className={`w-8 h-8 mb-3 ${
                    formData.company_type === type.id ? 'text-neon-cyan' : 'text-slate-400'
                  }`} />
                  <h4 className="font-semibold text-white mb-1">{type.label}</h4>
                  <p className="text-sm text-slate-500">{type.desc}</p>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!formData.company_type}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </motion.div>
        )}

        {/* Step 2: Contact & Company Info */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3 className="text-xl font-heading font-semibold text-white mb-6">
              Tell us about your organization
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="Acme Healthcare"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="https://company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Service Area
                </label>
                <input
                  type="text"
                  name="service_area"
                  value={formData.service_area}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="e.g., Greater Atlanta, Statewide"
                />
              </div>
              {(formData.company_type === 'transportation_company' || formData.company_type === 'broker') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Fleet Size
                    </label>
                    <select
                      name="fleet_size"
                      value={formData.fleet_size}
                      onChange={handleChange}
                      className="w-full input-dark"
                    >
                      <option value="">Select...</option>
                      {fleetSizeOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Current Trip Volume
                    </label>
                    <select
                      name="current_trip_volume"
                      value={formData.current_trip_volume}
                      onChange={handleChange}
                      className="w-full input-dark"
                    >
                      <option value="">Select...</option>
                      {tripVolumeOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 btn-secondary"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={!formData.contact_name || !formData.email || !formData.company_name}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Services & Submit */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3 className="text-xl font-heading font-semibold text-white mb-6">
              What services are you interested in?
            </h3>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {serviceOptions.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => handleServiceToggle(service.id)}
                  className={`p-4 rounded-xl border flex items-center space-x-3 transition-all ${
                    formData.services_interested.includes(service.id)
                      ? 'bg-neon-cyan/10 border-neon-cyan/50'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <service.icon className={`w-5 h-5 ${
                    formData.services_interested.includes(service.id) ? 'text-neon-cyan' : 'text-slate-400'
                  }`} />
                  <span className={formData.services_interested.includes(service.id) ? 'text-white' : 'text-slate-300'}>
                    {service.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                How did you hear about MedexOVA?
              </label>
              <select
                name="how_heard"
                value={formData.how_heard}
                onChange={handleChange}
                className="w-full input-dark"
              >
                <option value="">Select...</option>
                {howHeardOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Additional Information
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full input-dark resize-none"
                placeholder="Tell us about your current challenges, goals, or any questions you have..."
              />
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 btn-primary flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Application</span>
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </form>
    </div>
  );
};

export default PartnerApplicationForm;
